// Изобаты (линии равных глубин) на карте кабинета.
//
// Зачем. Поле индекса отрыва без изобат читается как абстрактное пятно: весь
// смысл картинки в том, что нити идут ВДОЛЬ свалов глубин. Телеграм-карта
// `/planktondetach` рисует их всегда, кабинет обязан выглядеть так же.
//
// Цвета и начертания — один-в-один из
// `src/plankton_detachment/map_generator.py::ISOBATH_STYLES`. Толщины у
// matplotlib заданы в пунктах (0.5…1.5) — здесь они переведены в пиксели
// MapLibre с сохранением ГЛАВНОГО: чем глубже изобата, тем линия заметнее,
// 200/500/1000 м читаются как каркас рельефа, а 50/100 м — как подсказка.

import type { LayerPalette } from '../api'
import {
  ISOBATH_FALLBACK_COLOR,
  ISOBATH_LEVEL_COLORS,
} from '../brand/mapColors'
import { tokenColor } from './cssToken'
import { ISOBATH_PREFIX } from './layerOrder'

import { t } from '../i18n'

export interface IsobathStyle {
  color: string
  /** Толщина линии в пикселях на опорном зуме (см. isobathWidthExpr). */
  width: number
  /** Штриховка в единицах толщины линии; `null` — сплошная. */
  dash: [number, number] | null
}

/** Подпись изобаты — глубина и единица из каталога («50 м» / «50 m»).
 *  Раньше стояла в стиле готовой строкой, и «м» уезжала в английский кабинет. */
export function isobathLabel(depth: number): string {
  return `${Math.abs(depth)} ${t('units.m')}`
}

/** Глубины (в метрах, отрицательные) — те же, что у телеграм-карты. */
export const ISOBATH_DEPTHS = [-50, -100, -200, -500, -1000] as const

export const ISOBATH_STYLES: Record<number, IsobathStyle> = {
  // ':' у matplotlib — точки: короткий штрих, длинный пробел.
  [-50]: { color: ISOBATH_LEVEL_COLORS[-50], width: 0.8, dash: [0.5, 1.8] },
  // '--' — штрихи.
  [-100]: { color: ISOBATH_LEVEL_COLORS[-100], width: 1.0, dash: [2, 1.8] },
  [-200]: { color: ISOBATH_LEVEL_COLORS[-200], width: 1.2, dash: null },
  [-500]: { color: ISOBATH_LEVEL_COLORS[-500], width: 1.5, dash: null },
  [-1000]: { color: ISOBATH_LEVEL_COLORS[-1000], width: 1.8, dash: null },
}

/** Запасной стиль: серая волосяная линия. Глубина не из списка — не повод
 *  ронять слой (набор глубин задаёт бэкенд и может измениться раньше фронта). */
const FALLBACK_STYLE: IsobathStyle = {
  color: ISOBATH_FALLBACK_COLOR,
  width: 0.6,
  dash: null,
}

export function isobathStyle(depth: number): IsobathStyle {
  return ISOBATH_STYLES[depth] ?? FALLBACK_STYLE
}

/** Шкала глубин — ТА ЖЕ, что красит растр батиметрии.
 *
 *  Приезжает с сервера в каталоге слоёв (`palette` слоя `bathymetry`), а не
 *  живёт здесь константой. Раньше жила: восемь опор были ЗЕРКАЛОМ питоновских
 *  `DEPTH_COLORS`, и одну и ту же шкалу приходилось править в двух языках, а
 *  сторож сверял их постфактум. С бренд-паком копия стала прямо вредной: на
 *  домене партнёра линия 300 м рисовалась бы нашим тоном поверх его дна.
 *
 *  Пока каталог не приехал, цвета нет вовсе — и это честнее подмены: линия
 *  рисуется цветом акцента темы, то есть заметно «ещё не по шкале».
 */
export type DepthRamp = ReadonlyArray<readonly [number, readonly number[]]>

export function depthRamp(palette: LayerPalette | undefined): DepthRamp | null {
  if (!palette || palette.colors.length < 2) return null
  return palette.stops.map((d, i) => [d, palette.colors[i]] as const)
}

function _rgb(c: readonly number[]): string {
  return `rgb(${c[0]},${c[1]},${c[2]})`
}

/** Цвет изобаты по её глубине. Линейная интерполяция между опорами, за краями
 *  шкалы — крайний цвет (линий глубже её в данных нет, но сорваться в чёрное на
 *  случайном входе слой не должен).
 *
 *  Усечение к целому — как в питоновском `depth_to_color`: та же арифметика
 *  даёт тот же цвет, и линия ложится ровно в тон растра. */
export function isobathDepthColor(
  depth: number,
  palette: LayerPalette | undefined,
): string | null {
  const ramp = depthRamp(palette)
  if (!ramp) return null
  const d = Number.isFinite(depth) ? depth : 0
  const top = ramp[ramp.length - 1]
  if (d >= top[0]) return _rgb(top[1])
  for (let i = ramp.length - 1; i > 0; i--) {
    const [d1, c1] = ramp[i]
    const [d2, c2] = ramp[i - 1]
    if (d <= d1 && d >= d2) {
      const t = d2 === d1 ? 0 : (d - d1) / (d2 - d1)
      return _rgb([0, 1, 2].map(k => Math.trunc(c1[k] + t * (c2[k] - c1[k]))))
    }
  }
  return _rgb(ramp[0][1])
}

/** Глубина, на которой толщина линии перестаёт расти, — кромка шельфа.
 *
 *  Изобаты с 11.09.2026 идут до 6000 м, но толщина растёт только до тысячи:
 *  ниже линии идут через 500 м, и продолжи толщина расти, дно желоба легло бы
 *  жирной штриховкой поверх того, ради чего слой включают, — свала. */
const WIDTH_FULL_AT_M = 1000

/** Толщина линии по глубине: мелкие — подсказка, глубокие — каркас рельефа. Та
 *  же мысль, что у пятёрки телеграм-карты (0.8…1.8 px), только непрерывно. */
export function isobathDepthWidth(depth: number): number {
  const m = Math.min(WIDTH_FULL_AT_M, Math.abs(Number.isFinite(depth) ? depth : 0))
  return 0.7 + (m / WIDTH_FULL_AT_M) * 0.9
}

/** Приставка в ключе картинок-подписей раздела с шагом. Отличает их от подписей
 *  карты апвеллинга: те пишутся цветом своей линии, эти — тёмными чернилами, а
 *  кэш картинок в MapLibre общий и различает их только по имени. */
export const ISOBATH_RAMP_LABEL_PREFIX = 'isobath-label-ramp-'

// --------------------------------------------------------------------------- //
// Выражения слоёв раздела «Изобаты». Вынесены сюда из MapCanvas, потому что их
// правила проверяет не TypeScript, а движок — в рантайме. Здесь они доступны
// парсеру MapLibre в тестах (`isobathRamp.test.ts`).
// --------------------------------------------------------------------------- //

/** Цвет линии по глубине — шкала батиметрии. `interpolate` требует ВОЗРАСТАЮЩИХ
 *  входов; опоры палитры уже возрастают (от дна к поверхности).
 *
 *  Каталог ещё не приехал — возвращается токен акцента, а не наша зашитая
 *  шкала: чужую гамму под партнёрской картой лучше не рисовать вовсе. */
export function isobathRampColorExpr(palette: LayerPalette | undefined): unknown {
  const ramp = depthRamp(palette)
  if (!ramp) return tokenColor('--accent')
  return [
    'interpolate',
    ['linear'],
    ['get', 'depth'],
    ...ramp.flatMap(([d, c]) => [d, _rgb(c)]),
  ]
}

function _widthByDepth(): unknown {
  return [
    // Глубже кромки `interpolate` держит крайнее значение — ровно как
    // `isobathDepthWidth`.
    'interpolate', ['linear'], ['get', 'depth'],
    -WIDTH_FULL_AT_M, isobathDepthWidth(-WIDTH_FULL_AT_M),
    0, isobathDepthWidth(0),
  ]
}

/** Толщина линии: зум СНАРУЖИ, глубина внутри. Обратный порядок MapLibre не
 *  принимает — zoom обязан быть самым внешним `interpolate`.
 *
 *  `extra` (прибавка для подложки) вкладывается ВНУТРЬ, в каждый выход по зуму.
 *  Снаружи её прибавить нельзя: `['+', <zoom-interpolate>, 1.2]` — то же
 *  нарушение, движок такой стиль отвергает целиком. TypeScript этого не видит,
 *  ловит только парсер (`isobathRamp.test.ts`). */
function _rampWidthExpr(extra = 0): unknown {
  const w = _widthByDepth()
  const at = (k: number) => {
    const scaled = k === 1 ? w : ['*', w, k]
    return extra ? ['+', scaled, extra] : scaled
  }
  return ['interpolate', ['linear'], ['zoom'], 3, at(0.7), 7, at(1), 12, at(1.6)]
}

export function isobathRampWidthExpr(): unknown {
  return _rampWidthExpr()
}

/** Толщина белой подложки — всегда шире линии, иначе обводки не видно. */
export function isobathRampCasingWidthExpr(): unknown {
  return _rampWidthExpr(1.2)
}

/** Имя картинки-подписи для глубины — ТО ЖЕ, что собирает выражение слоя.
 *  Регистрация и поиск обязаны идти через одну функцию: разойдутся — подписи
 *  молча исчезнут, а линии останутся, и причину придётся искать глазами. */
export function isobathRampLabelImageId(depth: number): string {
  return `${ISOBATH_RAMP_LABEL_PREFIX}${Math.abs(depth)}`
}

/** Имя картинки-подписи по глубине фичи. Один symbol-слой на весь раздел вместо
 *  слоя на глубину: `icon-image` выражения принимает (в отличие от dasharray). */
export function isobathRampIconExpr(): unknown {
  return ['concat', ISOBATH_RAMP_LABEL_PREFIX, ['to-string', ['abs', ['get', 'depth']]]]
}

/** Глубины, реально пришедшие в ответе, — от мелкой к глубокой, без повторов.
 *
 *  Слои строятся ПО НИМ, а не по `ISOBATH_DEPTHS`. Раньше брали зашитую пятёрку
 *  (-50/-100/-200/-500/-1000) и вешали на каждый слой фильтр `depth == …` — а
 *  шаг задаёт человек, и набор уровней приходит какой угодно. На шаге 30 м
 *  бэкенд отдавал -30, -90, -150…: ни один в пятёрку не попадал, и карта
 *  оставалась пустой (замечено владельцем 2026-08-08). Запасной стиль для
 *  незнакомых глубин был, но до него дело не доходило — слоя не существовало.
 *
 *  Порядок важен: наложение идёт снизу вверх, поэтому заметные 500/1000 м
 *  должны добавляться последними и ложиться поверх мелких. */
export function isobathDepthsOf(data: GeoJSON.FeatureCollection): number[] {
  const seen = new Set<number>()
  for (const f of data.features ?? []) {
    const d = (f.properties as { depth?: unknown } | null)?.depth
    if (typeof d === 'number' && Number.isFinite(d)) seen.add(d)
  }
  return [...seen].sort((a, b) => b - a)
}

/** id слоя одной глубины. Слой на глубину, а не один общий: `line-dasharray`
 *  в MapLibre не принимает data-driven выражений, начертанием по свойству
 *  управлять нельзя. */
export function isobathLayerId(key: string, depth: number): string {
  return `${ISOBATH_PREFIX}${key}-${Math.abs(depth)}`
}

/** id слоёв раздела с шагом. Там глубин может быть сотня, но слоя всего три:
 *  подложка-обводка, сами линии (цвет и толщина — выражением по `depth`) и
 *  подписи (`icon-image` тоже выражением). Слой на глубину нужен только там,
 *  где есть штрихи, — а штрихи остались лишь у пятёрки телеграм-карты. */
export function isobathRampLayerId(key: string, part: 'casing' | 'line' | 'label'): string {
  return `${ISOBATH_PREFIX}${key}-ramp-${part}`
}

/** id слоя подписей глубины для одной изобаты.
 *  Отдельный слой на глубину — по той же причине, что и у линий: подпись берётся
 *  из готовой картинки, своей на каждую глубину. */
export function isobathLabelLayerId(key: string, depth: number): string {
  return `${isobathLayerId(key, depth)}-label`
}

/** С какого зума подписывать глубины. На обзорных масштабах в кадр попадает весь
 *  Дальний Восток, изобаты идут вплотную, и подписи превращаются в кашу — там
 *  глубину называет легенда, а не карта. */
export const ISOBATH_LABEL_MIN_ZOOM = 7

/** Ключ зума для запроса: целое. Допуск упрощения на бэкенде считается по зуму,
 *  и без округления каждый пиксель зума менял бы URL запроса — пан и инерция
 *  зума гнали бы шторм ответов по сотне килобайт. */
export function isobathZoomStep(zoom: number): number {
  return Math.max(0, Math.round(zoom))
}

/** Толщина линии по зуму: на обзорных изобаты тоньше (их там много и они
 *  сливаются), на рабочих — в номинал, на предельных чуть заметнее. */
export function isobathWidthExpr(width: number) {
  return [
    'interpolate',
    ['linear'],
    ['zoom'],
    3,
    width * 0.7,
    7,
    width,
    12,
    width * 1.6,
  ]
}
